/**
 * System Settings Page
 *
 * System configuration page for admins to manage application settings,
 * feature flags, notifications, and integration configurations.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/forms/Input';

export const SystemSettingsPage: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);

  // Mock settings state
  const [settings, setSettings] = useState({
    systemName: 'HALMAS - Halal Management System',
    supportEmail: 'support@halmas.gov.my',
    defaultCurrency: 'MYR',
    defaultLanguage: 'en-US',
    applicationFee: '500',
    certificateValidity: '2',
    notifyApplicationSubmitted: true,
    notifyAuditScheduled: true,
    notifyCertificateIssued: true,
    enablePayments: false,
    enableMultiLanguage: false,
    enableTraining: false,
    paymentGatewayKey: 'sk_test_xxxxxxxxxxxxx',
    emailServiceConfig: 'smtp.example.com',
    smsServiceConfig: '+601234567890',
  });

  const [showPaymentKey, setShowPaymentKey] = useState(false);

  const handleSave = () => {
    alert(t('admin:settings.settingsSaved'));
  };

  const handleReset = () => {
    if (window.confirm(t('admin:settings.confirmReset'))) {
      // Reset to defaults (mock)
      alert(t('admin:settings.settingsReset'));
    }
  };

  return (
    <PageLayout
      pageTitle={t('admin:settings.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('admin:dashboard.title'), href: '/admin/dashboard' },
        { label: t('admin:settings.title') },
      ]}
    >
      <div className="space-y-6">
        {/* General Settings */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">{t('admin:settings.general')}</h3>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin:settings.systemName')}
                </label>
                <Input
                  name="systemName"
                  value={settings.systemName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSettings({ ...settings, systemName: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin:settings.supportEmail')}
                </label>
                <Input
                  type="email"
                  name="supportEmail"
                  value={settings.supportEmail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSettings({ ...settings, supportEmail: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin:settings.defaultCurrency')}
                </label>
                <select
                  value={settings.defaultCurrency}
                  onChange={(e) => setSettings({ ...settings, defaultCurrency: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="MYR">MYR - Malaysian Ringgit</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="SGD">SGD - Singapore Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin:settings.defaultLanguage')}
                </label>
                <select
                  value={settings.defaultLanguage}
                  onChange={(e) => setSettings({ ...settings, defaultLanguage: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="en-US">English (US)</option>
                  <option value="ms-MY">Malay (Coming Soon)</option>
                  <option value="zh-CN">Chinese Simplified (Coming Soon)</option>
                  <option value="ar-SA">Arabic (Coming Soon)</option>
                </select>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Application Settings */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">{t('admin:settings.application')}</h3>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin:settings.applicationFee')} (MYR)
                </label>
                <Input
                  type="number"
                  name="applicationFee"
                  value={settings.applicationFee}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSettings({ ...settings, applicationFee: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin:settings.certificateValidity')}
                </label>
                <Input
                  type="number"
                  name="certificateValidity"
                  value={settings.certificateValidity}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSettings({ ...settings, certificateValidity: e.target.value })
                  }
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin:settings.autoAssignment')}
                </label>
                <Input
                  name="autoAssignment"
                  value="Round-robin distribution (Mock)"
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">Feature coming soon</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Email Notifications */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">{t('admin:settings.notifications')}</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <span className="text-sm font-medium text-gray-700">
                  {t('admin:settings.applicationSubmitted')}
                </span>
                <input
                  type="checkbox"
                  checked={settings.notifyApplicationSubmitted}
                  onChange={(e) =>
                    setSettings({ ...settings, notifyApplicationSubmitted: e.target.checked })
                  }
                  className="w-5 h-5"
                />
              </label>

              <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <span className="text-sm font-medium text-gray-700">
                  {t('admin:settings.auditScheduled')}
                </span>
                <input
                  type="checkbox"
                  checked={settings.notifyAuditScheduled}
                  onChange={(e) =>
                    setSettings({ ...settings, notifyAuditScheduled: e.target.checked })
                  }
                  className="w-5 h-5"
                />
              </label>

              <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <span className="text-sm font-medium text-gray-700">
                  {t('admin:settings.certificateIssued')}
                </span>
                <input
                  type="checkbox"
                  checked={settings.notifyCertificateIssued}
                  onChange={(e) =>
                    setSettings({ ...settings, notifyCertificateIssued: e.target.checked })
                  }
                  className="w-5 h-5"
                />
              </label>
            </div>
          </CardBody>
        </Card>

        {/* Feature Flags */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">{t('admin:settings.features')}</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    {t('admin:settings.enablePayments')}
                  </span>
                  <p className="text-xs text-gray-500">Enable payment processing integration</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.enablePayments}
                  onChange={(e) =>
                    setSettings({ ...settings, enablePayments: e.target.checked })
                  }
                  className="w-5 h-5"
                />
              </label>

              <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    {t('admin:settings.enableMultiLanguage')}
                  </span>
                  <p className="text-xs text-gray-500">Enable multi-language support</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.enableMultiLanguage}
                  onChange={(e) =>
                    setSettings({ ...settings, enableMultiLanguage: e.target.checked })
                  }
                  className="w-5 h-5"
                />
              </label>

              <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    {t('admin:settings.enableTraining')}
                  </span>
                  <p className="text-xs text-gray-500">Enable training module</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.enableTraining}
                  onChange={(e) =>
                    setSettings({ ...settings, enableTraining: e.target.checked })
                  }
                  className="w-5 h-5"
                />
              </label>
            </div>
          </CardBody>
        </Card>

        {/* Integration Settings */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">{t('admin:settings.integrations')}</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin:settings.paymentGatewayKey')}
                </label>
                <div className="flex gap-2">
                  <Input
                    type={showPaymentKey ? 'text' : 'password'}
                    name="paymentGatewayKey"
                    value={settings.paymentGatewayKey}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSettings({ ...settings, paymentGatewayKey: e.target.value })
                    }
                  />
                  <Button
                    variant="outline"
                    onClick={() => setShowPaymentKey(!showPaymentKey)}
                  >
                    {showPaymentKey ? t('admin:settings.hideKey') : t('admin:settings.showKey')}
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin:settings.emailServiceConfig')}
                </label>
                <Input
                  name="emailServiceConfig"
                  value={settings.emailServiceConfig}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSettings({ ...settings, emailServiceConfig: e.target.value })
                  }
                  placeholder="smtp.example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin:settings.smsServiceConfig')}
                </label>
                <Input
                  name="smsServiceConfig"
                  value={settings.smsServiceConfig}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSettings({ ...settings, smsServiceConfig: e.target.value })
                  }
                  placeholder="+60123456789"
                />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Action Buttons */}
        <Card>
          <CardBody>
            <div className="flex gap-4">
              <Button variant="outline" onClick={handleReset} fullWidth>
                {t('admin:settings.resetDefaults')}
              </Button>
              <Button onClick={handleSave} fullWidth>
                💾 {t('admin:settings.saveSettings')}
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </PageLayout>
  );
};
